class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // [2026-09-19 Sydney] Keep keyboard focus inside the open drawer. The Essential Upsell widget (and the
    // cartUpdate row refresh) replace buttons that may hold focus; focus then fell to <body>, outside this
    // aria-modal dialog, and Escape stopped working. If that happens while open, return it to the dialog.
    // Escape closes the open drawer from anywhere inside it, including the moment focus has just fallen to
    // <body> (where the drawer's own keyup listener can't hear it). An open cart-note <details> keeps its
    // own Escape. close() hands focus back to the trigger.
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.classList.contains('active')) return;
      const active = document.activeElement;
      const inside = active && this.contains(active);
      const lost = !active || active === document.body || active === document.documentElement;
      if (!inside && !lost) return;
      if (inside && active.closest('details[open]') && this.contains(active.closest('details[open]'))) return;
      this.close();
    });

    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget) return;
      setTimeout(() => {
        if (!this.classList.contains('active')) return;
        const active = document.activeElement;
        if (active && active !== document.body && active !== document.documentElement) return;
        (this.querySelector('.drawer__inner') || this.querySelector('.drawer__close'))?.focus();
      });
    });

    // [2026-09-19 16:05 AEST] Focus can also fall to <body> with NO focusout at all: when the focused node is
    // removed from the DOM (Essential Upsell re-renders inside the open drawer about 5 s after an add; the
    // cartUpdate row refresh does the same), Chrome fires no focusout, so the guard above never runs. Seen by
    // chief-of-staff-31 on the plush page in 1 of 2 runs: focus on <body> ~5 s after the add. Watch the drawer
    // subtree while it is open; after each mutation batch, if focus is on <body>, put it back on the dialog
    // (.drawer__inner, else the close button). Nothing happens while focus is anywhere real. Fable review;
    // rollback copy: chief-of-staff outputs/tmg-home-v4/fable-review-2026-09-19/rollback/assets_cart-drawer.js.live
    this.focusCheckPending = false;
    this.focusObserver = new MutationObserver(() => {
      if (this.focusCheckPending || !this.classList.contains('active')) return;
      this.focusCheckPending = true;
      requestAnimationFrame(() => {
        this.focusCheckPending = false;
        if (!this.classList.contains('active')) return;
        const active = document.activeElement;
        if (active && active !== document.body && active !== document.documentElement) return;
        // .drawer__inner only takes focus if it carries a tabindex, so fall through to the heading, then close.
        for (const target of [this.querySelector('.drawer__inner'), this.querySelector('.drawer__heading'), this.querySelector('.drawer__close')]) {
          if (!target) continue;
          target.focus();
          if (document.activeElement === target) return;
        }
      });
    });
    this.focusObserver.observe(this, { childList: true, subtree: true });

    // [2026-09-19 15:40 AEST] Measured on live after the observer shipped: with a fresh page load, the first add
    // still lost focus to <body> about 8 s later in 18 of 20 runs (focus-drop-after2.json), so the node that
    // goes is not in this element's light DOM (an app widget re-rendering inside its own shadow root; a
    // subtree observer cannot see that). While the drawer is open, also check every 300 ms and put focus
    // back on the dialog if it is on <body>. Cleared on close. Same rollback copy as above.
    this.focusPoll = null;
    this.startFocusPoll = () => {
      if (this.focusPoll) return;
      this.focusPoll = setInterval(() => {
        if (!this.classList.contains('active')) { clearInterval(this.focusPoll); this.focusPoll = null; return; }
        const active = document.activeElement;
        if (active && active !== document.body && active !== document.documentElement) return;
        for (const target of [this.querySelector('.drawer__inner'), this.querySelector('.drawer__heading'), this.querySelector('.drawer__close')]) {
          if (!target) continue;
          target.focus();
          if (document.activeElement === target) return;
        }
      }, 300);
    };
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    const trap = () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };
    // [2026-09-19 Sydney] Under reduced motion the v5 sheet sets `transition: none` on everything, so
    // transitionend never fires and focus stayed behind the open (aria-modal) drawer. Trap right after
    // the drawer shows in that case; with motion on, focus still moves on transitionend as before.
    // Rollback: C:/GIT/chief-of-staff/outputs/tmg-home-v4/interaction-fixes/rollback/cart-drawer.js.live
    // With motion on, a transition that is interrupted or never runs also never sends transitionend, so a
    // timeout just past the 250ms drawer transition moves focus in anyway; whichever comes first wins.
    let trapped = false;
    const trapOnce = () => {
      if (trapped || !this.classList.contains('active')) return;
      trapped = true;
      trap();
    };
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setTimeout(() => requestAnimationFrame(trapOnce));
    } else {
      this.addEventListener('transitionend', trapOnce, { once: true });
      setTimeout(trapOnce, 450);
    }

    document.body.classList.add('overflow-hidden');
    setTimeout(() => this.startFocusPoll(), 500);
  }

  close() {
    this.classList.remove('active');
    if (this.focusPoll) { clearInterval(this.focusPoll); this.focusPoll = null; }
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
